import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Flag } from "lucide-react";
import { EmptyState, PageHeader } from "../../components/common";
import { FlaggedPaymentsPanel } from "../../components/FlaggedPaymentsPanel";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, errMsg, fmtINR } from "../../lib/api";
import { FLAGS_CHANGED_EVENT } from "../../lib/flaggedCount";

/**
 * Phase 190 — Super Admin › Flagged Payments.
 * Pick a municipality (open counts shown in the picker) and work its flagged-payments register with the same powers as
 * its Municipal Admin: comment, correct the demand (Phase 188 dialog, platform routes) or mark the flag reviewed.
 */
export default function SuperFlaggedPayments() {
  const [searchParams, setSearchParams] = useSearchParams();
  const code = searchParams.get("muni") || "";
  const [summary, setSummary] = useState(null);

  const loadSummary = () => api.get("/platform/flagged-payments/summary").then((r) => setSummary(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    loadSummary();
    window.addEventListener(FLAGS_CHANGED_EVENT, loadSummary);
    return () => window.removeEventListener(FLAGS_CHANGED_EVENT, loadSummary);
  }, []);

  const munis = summary?.municipalities || [];
  const current = munis.find((m) => m.code === code);
  const pick = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("muni", v); else next.delete("muni");
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="super-flagged-payments-page">
      <PageHeader title="Flagged Payments"
        subtitle="Excess payments awaiting review in each municipality. Correct the demand or mark the flag reviewed — the excess stays as advance credit until the demand catches up.">
        {summary && (
          <Badge variant="outline" className={`text-xs ${summary.open ? "bg-red-50 text-red-800 border-red-200" : ""}`} data-testid="super-flagged-total-open">
            <Flag className="h-3 w-3 mr-1" /> {summary.open} open · {fmtINR(summary.remaining)} unreconciled
          </Badge>
        )}
      </PageHeader>

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4 grid gap-3 sm:grid-cols-[minmax(0,320px)_1fr] items-end">
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1"><Building2 className="h-3.5 w-3.5" /> Municipality</Label>
            <Select value={code} onValueChange={pick}>
              <SelectTrigger data-testid="super-flagged-muni-select"><SelectValue placeholder={summary ? "Choose a municipality" : "Loading…"} /></SelectTrigger>
              <SelectContent>
                {munis.map((m) => (
                  <SelectItem key={m.code} value={m.code} data-testid={`super-flagged-muni-${m.code}`}>
                    {m.name} <span className="text-muted-foreground">({m.code.toUpperCase()})</span>{m.open ? ` · ${m.open} open` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {summary && (
            <div className="flex flex-wrap gap-1.5" data-testid="super-flagged-muni-chips">
              {munis.map((m) => (
                <button key={m.code} type="button" onClick={() => pick(m.code)} data-testid={`super-flagged-chip-${m.code}`}
                  className={`rounded-full border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    m.code === code ? "bg-primary text-primary-foreground border-primary" : m.open ? "bg-red-50 text-red-800 border-red-200 hover:bg-red-100" : "text-muted-foreground hover:bg-muted"}`}>
                  {m.name} · {m.open} open
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {code ? (
        <FlaggedPaymentsPanel code={code} mode="platform" testId="super-flagged" muniName={current?.name}
          propertyLink={(f) => `/super/demand-corrections?muni=${code}&pin=${encodeURIComponent(f.pin || "")}`} />
      ) : (
        <Card className="shadow-sm"><CardContent className="p-0">
          <EmptyState title="Choose a municipality" subtitle="Flagged payments are kept per municipality. Pick one above to review its register." />
        </CardContent></Card>
      )}
    </div>
  );
}
